import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import IconWrapper from '../../IconWrapper';
import { FiMoreVertical, FiTrash2, FiExternalLink, FiZap, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Simulation } from './SimulationCard';
import { getSeverityColor } from '../../../utils/severityColors';
import { Timestamp } from '../../ui/timestamp';

interface SimulationsTableByDayProps {
  simulations: Simulation[];
  onViewSignal: (signalId: string) => void;
  onDelete?: (simulationId: string) => void;
  onRowClick: (simulationId: string) => void;
  isLoading?: boolean;
}

const groupSimulationsByDayAndSignal = (simulations: Simulation[]): Map<string, Map<string, Simulation[]>> => {
  const grouped = new Map<string, Map<string, Simulation[]>>();

  simulations.forEach((simulation) => {
    // Use ISO date string (YYYY-MM-DD) for reliable grouping and sorting
    const date = new Date(simulation.created_at);
    const dateKey = date.toISOString().split('T')[0];
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, new Map<string, Simulation[]>());
    }
    
    const dayGroup = grouped.get(dateKey)!;
    const signalId = simulation.signal_id;
    
    if (!dayGroup.has(signalId)) {
      dayGroup.set(signalId, []);
    }
    
    dayGroup.get(signalId)!.push(simulation);
  });

  // Sort simulations within each signal group by created_at (most recent first)
  grouped.forEach((dayGroup) => {
    dayGroup.forEach((sims) => {
      sims.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });
  });

  return grouped;
};

// Get color for outcome likelihood indicator
// Yellow if not processed yet, red for upheld, green for not upheld
const getOutcomeColor = (simulation: Simulation): string => {
  // Yellow if not completed yet
  if (simulation.status !== 'completed') {
    return '#FACC15'; // Tailwind yellow-400
  }

  const outcome = simulation.predicted_outcome;
  const confidence = simulation.confidence_score;

  if (!outcome || confidence === undefined || confidence === null) {
    return '#9CA3AF'; // Gray for unknown/missing data
  }

  // Handle both 0-1 and 0-100 formats
  const confidencePercent = confidence > 1 ? Math.round(confidence) : Math.round(confidence * 100);
  const clampedConfidence = Math.max(0, Math.min(100, confidencePercent));
  
  if (outcome?.toLowerCase() === 'upheld' || outcome?.toLowerCase() === 'partially_upheld') {
    // Red for upheld - always red/orange, higher confidence = darker red
    // Map confidence to severity: 0% -> 70 (orange), 100% -> 90 (dark red)
    // This ensures we always stay in red/orange range (70-90 severity)
    const severityScore = 70 + (clampedConfidence / 100) * 20;
    return getSeverityColor(severityScore);
  } else {
    // Green for not upheld - always green, higher confidence = darker green
    // Map confidence to inverted severity: 100% -> 10 (dark green), 0% -> 50 (light green)
    // This ensures we always stay in green range (0-50 severity = green shades)
    const severityScore = 50 - (clampedConfidence / 100) * 40;
    return getSeverityColor(severityScore);
  }
};

const statusBadgeClasses = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-700';
    case 'processing':
      return 'bg-yellow-50 border-yellow-200 text-yellow-700';
    case 'failed':
      return 'bg-red-50 border-red-200 text-red-700';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-700';
  }
};

const SimulationsTableByDay: React.FC<SimulationsTableByDayProps> = ({
  simulations,
  onViewSignal,
  onDelete,
  onRowClick,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500">Loading simulations...</p>
      </div>
    );
  }

  if (simulations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-gray-500 mb-2">No cases found</p>
      </div>
    );
  }

  const groupedSimulations = groupSimulationsByDayAndSignal(simulations);
  
  // Sort days by date (most recent first) - dateKey is ISO format YYYY-MM-DD
  const sortedDays = Array.from(groupedSimulations.keys()).sort((a, b) => {
    return b.localeCompare(a); // ISO date strings sort correctly lexicographically
  });

  const toggleExpand = (dateKey: string, signalId: string) => {
    const groupKey = `${dateKey}-${signalId}`;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const isExpanded = (dateKey: string, signalId: string): boolean => {
    return expandedGroups.has(`${dateKey}-${signalId}`);
  };

  const handleRowClick = (simulationId: string) => {
    onRowClick(simulationId);
  };

  const handleDelete = async (e: React.MouseEvent, simulationId: string) => {
    e.stopPropagation();
    if (onDelete) {
      setDeletingId(simulationId);
      try {
        await onDelete(simulationId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleViewSignal = (e: React.MouseEvent, signalId: string) => {
    e.stopPropagation();
    onViewSignal(signalId);
  };

  return (
    <div className="space-y-8">
      {sortedDays.map((dateKey) => {
        const daySignalGroups = groupedSimulations.get(dateKey)!;
        const signalIds = Array.from(daySignalGroups.keys());
        
        // Calculate total simulations for the day
        let totalSimulations = 0;
        daySignalGroups.forEach((sims) => {
          totalSimulations += sims.length;
        });
        
        // Get display date from first simulation
        const firstSimulation = daySignalGroups.get(signalIds[0])?.[0];

        return (
          <div key={dateKey} className="space-y-4">
            {/* Day header */}
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                {firstSimulation ? (
                  <Timestamp value={firstSimulation.created_at} format="date-only" />
                ) : (
                  dateKey
                )}
              </h2>
              <Badge variant="outline" className="text-xs">
                {totalSimulations} {totalSimulations === 1 ? 'case' : 'cases'}
              </Badge>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden w-full">
              <style>{`
                .simulations-table-wrapper div[data-slot="table-container"] {
                  overflow-x: hidden !important;
                  width: 100% !important;
                }
              `}</style>
              <div className="simulations-table-wrapper">
                <Table className="w-full table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead className="w-[65px] whitespace-nowrap">Time</TableHead>
                      <TableHead className="w-[75px] whitespace-nowrap">Outcome</TableHead>
                      <TableHead className="w-auto">Signal</TableHead>
                      <TableHead className="w-[85px] whitespace-nowrap">Status</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signalIds.map((signalId) => {
                      const signalSimulations = daySignalGroups.get(signalId)!;
                      const mostRecent = signalSimulations[0];
                      const pastSimulations = signalSimulations.slice(1);
                      const hasMultiple = signalSimulations.length > 1;
                      const groupKey = `${dateKey}-${signalId}`;
                      const expanded = isExpanded(dateKey, signalId);

                      const renderSimulationRow = (simulation: Simulation, isExpandedRow: boolean = false) => {
                        const confidencePercent = simulation.confidence_score !== undefined
                          ? (simulation.confidence_score > 1 
                              ? Math.round(simulation.confidence_score) 
                              : Math.round(simulation.confidence_score * 100))
                          : 0;
                        const outcomeColor = getOutcomeColor(simulation);

                        return (
                          <TableRow
                            key={simulation.id}
                            className={`cursor-pointer hover:bg-muted/50 transition-colors ${
                              deletingId === simulation.id ? 'opacity-50 pointer-events-none' : ''
                            } ${isExpandedRow ? 'bg-muted/30' : ''}`}
                            onClick={() => handleRowClick(simulation.id)}
                          >
                            <TableCell className="whitespace-nowrap">
                              {isExpandedRow ? (
                                <div className="w-4"></div>
                              ) : hasMultiple ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-muted"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(dateKey, signalId);
                                  }}
                                  title={expanded ? 'Collapse' : 'Expand'}
                                >
                                  <IconWrapper 
                                    icon={expanded ? FiChevronDown : FiChevronRight} 
                                    className="h-4 w-4 text-muted-foreground" 
                                  />
                                </Button>
                              ) : (
                                <div className="w-4"></div>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <div className="text-sm">
                                <Timestamp value={simulation.created_at} format="time-only" />
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <div className="flex items-center justify-center">
                                <div
                                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                                  style={{ backgroundColor: outcomeColor }}
                                >
                                  <span className="text-sm font-bold text-white leading-none">
                                    {confidencePercent}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="min-w-0 overflow-hidden">
                              <div className="min-w-0 pr-2 overflow-hidden">
                                <div className="font-medium text-sm truncate">
                                  {simulation.signal_summary || simulation.name || `Simulation ${simulation.id.slice(0, 8)}`}
                                </div>
                                {simulation.summary && (
                                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                    {simulation.summary}
                                  </div>
                                )}
                                {!isExpandedRow && hasMultiple && !expanded && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    <Badge variant="outline" className="text-xs">
                                      +{pastSimulations.length} more
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <Badge variant="outline" className={statusBadgeClasses(simulation.status)}>
                                {simulation.status}
                              </Badge>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-muted"
                                onClick={(e) => handleViewSignal(e, simulation.signal_id)}
                                title="View Signal"
                              >
                                <IconWrapper icon={FiZap} className="h-4 w-4 text-primary" />
                              </Button>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <IconWrapper icon={FiMoreVertical} className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => handleViewSignal(e, simulation.signal_id)}>
                                    <IconWrapper icon={FiExternalLink} className="h-4 w-4 mr-2" />
                                    View Signal
                                  </DropdownMenuItem>
                                  {onDelete && (
                                    <DropdownMenuItem
                                      onClick={(e) => handleDelete(e, simulation.id)}
                                      className="text-red-600"
                                    >
                                      <IconWrapper icon={FiTrash2} className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      };

                      return (
                        <React.Fragment key={signalId}>
                          {/* Main row - most recent simulation */}
                          {renderSimulationRow(mostRecent, false)}
                          
                          {/* Expanded rows - past simulations */}
                          {expanded && pastSimulations.map((simulation) => 
                            renderSimulationRow(simulation, true)
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SimulationsTableByDay;

